import { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const PRODUCTS = [
  {
    id: 'magic-design',
    icon: 'ri-magic-line',
    tag: 'Design Suite',
    accent: 'teal',
    name: 'Magic Designing Software',
    tagline: 'Professional AI-Powered Design at Your Fingertips',
    desc: 'A professional-grade design suite powered by AI that enables creators, marketers, and businesses to build stunning visuals, branding assets, and UI mockups — no design degree required. The intuitive drag-and-drop canvas combined with smart AI layout suggestions makes brand-quality design accessible to everyone.',
    features: [
      { icon: 'ri-layout-3-line', label: 'AI layout suggestions' },
      { icon: 'ri-shield-user-line', label: 'Brand kit management' },
      { icon: 'ri-file-transfer-line', label: 'Export to any format' },
      { icon: 'ri-team-line', label: 'Team collaboration' },
      { icon: 'ri-palette-line', label: 'Smart color palettes' },
      { icon: 'ri-font-size', label: 'Typography system' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20graphic%20design%20software%20application%20interface%20with%20canvas%20drag%20and%20drop%20tools%20color%20palette%20layers%20panel%20creative%20design%20AI%20suggestions%20branding%20tool%20clean%20white%20background%20teal%20accent%20modern%20minimalist%20UI&width=700&height=450&seq=magic-design-prod-001&orientation=landscape',
  },
  {
    id: 'photo-press',
    icon: 'ri-image-edit-line',
    tag: 'Photo + Publishing',
    accent: 'amber',
    name: 'Magic Photo Press & Ebook Suite',
    tagline: 'From Raw Photo to Published Masterpiece',
    desc: 'The ultimate creative platform that unifies professional photo editing with a full-featured ebook and digital publication builder. AI-powered retouching, noise reduction, and color grading combined with a drag-and-drop ebook layout engine — publish to PDF, EPUB, and online platforms in one seamless workflow.',
    features: [
      { icon: 'ri-magic-line', label: 'AI photo retouching' },
      { icon: 'ri-book-open-line', label: 'Ebook layout builder' },
      { icon: 'ri-file-pdf-line', label: 'Multi-format export' },
      { icon: 'ri-cloud-upload-line', label: 'One-click publishing' },
      { icon: 'ri-printer-line', label: 'Print-ready output' },
      { icon: 'ri-history-line', label: 'Version history' },
    ],
    image: 'https://readdy.ai/api/search-image?query=photo%20editing%20and%20ebook%20publishing%20software%20interface%20showing%20photo%20editor%20with%20retouching%20tools%20adjustment%20sliders%20color%20grading%20and%20ebook%20layout%20builder%20side%20by%20side%20modern%20professional%20amber%20teal%20accent%20UI%20on%20desktop&width=700&height=450&seq=photo-press-prod-001&orientation=landscape',
  },
  {
    id: 'ship-chandling',
    icon: 'ri-ship-line',
    tag: 'Maritime',
    accent: 'teal',
    name: 'Ship Chandling Quotation System',
    tagline: 'Streamline Maritime Supply Chain Operations',
    desc: 'An intelligent quotation management platform purpose-built for ship chandlers and maritime suppliers. Issue, track, and manage quotes with automated supplier comparison, approval workflows, compliance reporting, and full audit trails — reducing quote turnaround time by up to 70%.',
    features: [
      { icon: 'ri-file-list-3-line', label: 'Automated quotation builder' },
      { icon: 'ri-exchange-line', label: 'Supplier comparison engine' },
      { icon: 'ri-check-double-line', label: 'Approval workflow' },
      { icon: 'ri-file-chart-line', label: 'Compliance reporting' },
      { icon: 'ri-anchor-line', label: 'Port & vessel management' },
      { icon: 'ri-time-line', label: 'Real-time tracking' },
    ],
    image: 'https://readdy.ai/api/search-image?query=maritime%20ship%20chandling%20quotation%20management%20software%20dashboard%20interface%20with%20data%20tables%20quotation%20forms%20shipping%20vessel%20port%20operations%20inventory%20supply%20chain%20professional%20UI%20clean%20white%20background%20teal%20accents%20enterprise%20application&width=700&height=450&seq=ship-chandling-prod-001&orientation=landscape',
  },
  {
    id: 'inventory',
    icon: 'ri-store-3-line',
    tag: 'Operations',
    accent: 'amber',
    name: 'Inventory Management System',
    tagline: 'AI-Powered Stock Control for Modern Businesses',
    desc: 'A smart, scalable inventory platform for warehouses, retail, and manufacturing. Real-time stock tracking across multiple locations, AI demand forecasting, automated reorder points, and full integration with barcode scanners and RFID readers — eliminating stockouts and overstock situations.',
    features: [
      { icon: 'ri-bar-chart-2-line', label: 'Real-time stock tracking' },
      { icon: 'ri-robot-line', label: 'AI demand forecasting' },
      { icon: 'ri-building-2-line', label: 'Multi-warehouse support' },
      { icon: 'ri-qr-code-line', label: 'Barcode & RFID integration' },
      { icon: 'ri-alert-line', label: 'Smart reorder alerts' },
      { icon: 'ri-file-chart-line', label: 'Advanced reporting' },
    ],
    image: 'https://readdy.ai/api/search-image?query=inventory%20management%20system%20dashboard%20software%20showing%20real-time%20stock%20levels%20bar%20charts%20warehouse%20locations%20product%20catalog%20data%20tables%20teal%20green%20accent%20colors%20modern%20business%20UI%20professional%20clean%20white%20light%20background%20enterprise%20application&width=700&height=450&seq=inventory-prod-001&orientation=landscape',
  },
  {
    id: 'pos',
    icon: 'ri-shopping-cart-2-line',
    tag: 'Retail & Hospitality',
    accent: 'teal',
    name: 'POS Solution',
    tagline: 'Fast, Intuitive Point of Sale for Every Business',
    desc: 'A feature-rich Point of Sale system designed for retail, restaurants, cafés, and service businesses. Handle sales, payments, receipts, and reporting through a fast touchscreen interface with multi-terminal support, payment gateway integration, and a built-in loyalty and discounts engine.',
    features: [
      { icon: 'ri-computer-line', label: 'Multi-terminal support' },
      { icon: 'ri-line-chart-line', label: 'Real-time sales reporting' },
      { icon: 'ri-bank-card-line', label: 'Payment gateway integration' },
      { icon: 'ri-coupon-line', label: 'Loyalty & discounts engine' },
      { icon: 'ri-store-line', label: 'Product & category management' },
      { icon: 'ri-cloud-line', label: 'Cloud sync & backup' },
    ],
    image: 'https://readdy.ai/api/search-image?query=point%20of%20sale%20POS%20system%20interface%20touchscreen%20tablet%20with%20product%20grid%20payment%20options%20sales%20dashboard%20modern%20clean%20white%20UI%20design%20retail%20restaurant%20hospitality%20business%20application%20professional%20minimalist%20teal%20accent&width=700&height=450&seq=pos-prod-001&orientation=landscape',
  },
  {
    id: 'rfid',
    icon: 'ri-nfc-line',
    tag: 'Asset Tracking',
    accent: 'amber',
    name: 'RFID Based Solution',
    tagline: 'Enterprise Asset Tracking & Access Control',
    desc: 'An enterprise-grade RFID platform for real-time asset tracking, personnel access control, and end-to-end supply chain visibility. Deploy passive or active RFID infrastructure and manage everything through a unified intelligent dashboard with automated audit trails and compliance reporting.',
    features: [
      { icon: 'ri-map-pin-2-line', label: 'Real-time asset tracking' },
      { icon: 'ri-door-lock-line', label: 'Access control integration' },
      { icon: 'ri-route-line', label: 'Supply chain visibility' },
      { icon: 'ri-clipboard-line', label: 'Automated audit trails' },
      { icon: 'ri-alarm-warning-line', label: 'Alert & notification engine' },
      { icon: 'ri-dashboard-line', label: 'Unified management dashboard' },
    ],
    image: 'https://readdy.ai/api/search-image?query=RFID%20asset%20tracking%20management%20system%20dashboard%20interface%20with%20real-time%20map%20showing%20asset%20locations%20tracking%20tags%20inventory%20automation%20data%20visualization%20charts%20professional%20enterprise%20UI%20clean%20white%20background%20amber%20teal%20accent&width=700&height=450&seq=rfid-prod-001&orientation=landscape',
  },
  {
    id: 'rfid-library',
    icon: 'ri-book-2-line',
    tag: 'Library Technology',
    accent: 'teal',
    name: 'RFID Library Management System',
    tagline: 'Modernize Your Library with RFID Intelligence',
    desc: 'Transform your library operations with a complete RFID-powered management system. Self-checkout kiosks, automated book returns, intelligent catalog management, member account handling, overdue tracking, and fine management — designed for academic libraries, public libraries, and corporate resource centers.',
    features: [
      { icon: 'ri-computer-line', label: 'RFID self-checkout kiosks' },
      { icon: 'ri-archive-line', label: 'Automated book returns' },
      { icon: 'ri-user-3-line', label: 'Member management' },
      { icon: 'ri-search-line', label: 'Full catalog management' },
      { icon: 'ri-time-line', label: 'Overdue & fine tracking' },
      { icon: 'ri-bar-chart-line', label: 'Circulation analytics' },
    ],
    image: 'https://readdy.ai/api/search-image?query=RFID%20library%20management%20system%20dashboard%20interface%20showing%20book%20catalog%20member%20accounts%20circulation%20history%20self%20service%20kiosk%20station%20modern%20clean%20UI%20teal%20green%20professional%20academic%20library%20technology%20application%20white%20background&width=700&height=450&seq=rfid-library-prod-001&orientation=landscape',
  },
];

function ProductCard({ prod, index }: { prod: typeof PRODUCTS[0]; index: number }) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);
  const isEven = index % 2 === 0;

  return (
    <section id={prod.id} ref={ref} className="py-20 bg-white border-b border-gray-100 last:border-0">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className={`grid lg:grid-cols-2 gap-14 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`} style={{ direction: isEven ? 'ltr' : 'rtl' }}>
          <div
            className={`rounded-2xl overflow-hidden border border-gray-100 transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : isEven ? 'opacity-0 -translate-x-8' : 'opacity-0 translate-x-8'}`}
            style={{ direction: 'ltr' }}
          >
            <div className="w-full h-80 lg:h-96">
              <img src={prod.image} alt={prod.name} className="w-full h-full object-cover object-top" />
            </div>
          </div>
          <div
            className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : isEven ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8'}`}
            style={{ direction: 'ltr' }}
          >
            <div className="flex items-center gap-3 mb-5">
              {/* <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${prod.accent === 'teal' ? 'bg-teal-100 text-teal-600' : 'bg-amber-100 text-amber-600'}`}>
                <i className={`${prod.icon} text-2xl`} />
              </div> */}
              {/* <span className={`text-xs font-semibold px-3 py-1 rounded-full ${prod.accent === 'teal' ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                {prod.tag}
              </span> */}
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-2">{prod.name}</h2>
            <p className={`text-sm font-semibold mb-4 ${prod.accent === 'teal' ? 'text-teal-600' : 'text-amber-600'}`}>{prod.tagline}</p>
            <p className="text-gray-500 text-base leading-relaxed mb-7">{prod.desc}</p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {prod.features.map((f) => (
                <div key={f.label} className="flex items-center gap-2.5 text-sm text-gray-700">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${prod.accent === 'teal' ? 'bg-teal-50 text-teal-500' : 'bg-amber-50 text-amber-500'}`}>
                    <i className={`${f.icon} text-base`} />
                  </div>
                  {f.label}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className={`cursor-pointer whitespace-nowrap inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-lg transition-colors text-sm ${
                  prod.accent === 'teal' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}
              >
                Request a Demo <i className="ri-arrow-right-line" />
              </Link>
              <Link to="/contact" className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 font-medium px-6 py-3 rounded-lg border border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600 transition-colors text-sm">
                Get Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ProductsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/70 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            {/* <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              Our Products
            </span> */}
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Proprietary <span className="text-teal-500">Software Suite</span>
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto mb-10">
              Purpose-built tools that solve real industry problems — available as standalone products or deeply integrated into your custom solution. All backed by 25 years of engineering expertise.
            </p>
            {/* Quick Nav */}
            <div className="flex flex-wrap justify-center gap-2">
              {PRODUCTS.map((p) => (
                <a
                  key={p.id}
                  href={`#${p.id}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(p.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                  className={`cursor-pointer whitespace-nowrap text-xs font-medium px-4 py-2 rounded-full border transition-colors ${p.accent === 'teal' ? 'border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100' : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'}`}
                >
                  {p.name}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        {PRODUCTS.map((prod, i) => (
          <ProductCard key={prod.id} prod={prod} index={i} />
        ))}

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Interested in Our Products?</h2>
            <p className="text-teal-100 text-base mb-8">Request a live demo or get pricing — our team will get back to you within 24 hours.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Book a Demo
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
